import React, { useEffect, useMemo } from 'react';
import { Button, Card } from 'react-bootstrap';
import { X } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWindows } from '@fortawesome/free-brands-svg-icons';
import usePasskey from '../../hooks/usePasskey';
import { getDeviceLabel, getPasskeyApiError } from '../../services/passkeyService';
import { showPmrConfirm, showPmrError, showPmrSuccess } from '../../utils/swalUtils';
import './Passkey.css';

const getCredentialId = (item) =>
    item?.credential_id || item?.CredentialId || item?.id || item?.credentialId || '';

const pickText = (...values) => {
    for (const value of values) {
        const text = String(value ?? '').trim();
        if (text && text !== 'undefined' && text !== 'null') return text;
    }
    return '';
};

const getCreatedAt = (item) =>
    item?.created_at || item?.CreatedAt || item?.created_on || item?.CreatedOn || item?.CreatedDate;

const formatWindowsHelloStamp = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
    });
};

const getPasskeyDisplayName = (item) => {
    const raw = pickText(
        item?.device_name,
        item?.DeviceName,
        item?.friendly_name,
        item?.FriendlyName,
        item?.name
    ) || getDeviceLabel();

    if (/\(.*\d.*\)/.test(raw)) return raw;
    if (/^windows hello$/i.test(raw)) {
        const stamp = formatWindowsHelloStamp(getCreatedAt(item));
        if (stamp) return `Windows Hello (${stamp})`;
    }
    return raw;
};

const DeviceIcon = () => <FontAwesomeIcon icon={faWindows} />;

const formatCreatedStamp = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'long' });
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const year = date.getFullYear() === new Date().getFullYear() ? '' : ` ${date.getFullYear()}`;
    return `${day} ${month}${year}, ${hours}:${minutes}`;
};

const getCreatedLine = (item) => {
    const created = formatCreatedStamp(getCreatedAt(item));
    if (created) return `Created: ${created}`;
    return '';
};

const PasskeyRegisterCard = ({ userName, emailAddress, userId, token }) => {
    const {
        supported,
        supportMessage,
        busy,
        error,
        passkeys,
        refreshPasskeys,
        removePasskey,
    } = usePasskey();

    useEffect(() => {
        if (token) {
            refreshPasskeys(token, userId, userName || emailAddress);
        }
    }, [token, userId, userName, emailAddress, refreshPasskeys]);

    const hasPasskeys = passkeys.length > 0;
    const intro = useMemo(() => {
        if (!supported) return supportMessage;
        return 'Manage passkey on your device for secure sign in without a password. You can still use your password anytime.';
    }, [supported, supportMessage]);

    const handleRemove = async (item) => {
        const credentialId = getCredentialId(item);
        const confirmed = await showPmrConfirm({
            title: 'Remove this passkey?',
            message: 'You can still sign in with your password or create a new passkey later.',
            confirmText: 'Remove',
            cancelText: 'Keep it',
            confirmButtonColor: 'var(--pmr-btn-primary)',
            cancelButtonColor: 'var(--pmr-danger)',
            customClass: { popup: 'passkey-remove-confirm' },
        });
        if (!confirmed?.isConfirmed) return;
        try {
            await removePasskey({ token, credentialId, userId, userName: userName || emailAddress });
            showPmrSuccess('Passkey removed. You can use your password to sign in on this device.');
        } catch (err) {
            showPmrError(getPasskeyApiError(err, 'Could not remove this saved sign-in.'));
        }
    };

    return (
        <Card className="passkey-card passkey-card--devices">
            <Card.Body>
                <h6 className="passkey-card__title">Manage your passkey</h6>
                <p className="passkey-card__copy">{intro}</p>

                {hasPasskeys ? (
                    <div className="passkey-devices">
                        <p className="passkey-devices__label">Your devices</p>
                        <ul className="passkey-list passkey-list--devices">
                            {passkeys.map((item, index) => {
                                const name = getPasskeyDisplayName(item);
                                const id = getCredentialId(item) || `${name}-${index}`;
                                const createdLine = getCreatedLine(item);
                                const lines = [createdLine].filter(Boolean);
                                return (
                                    <li key={id} className="passkey-list__item passkey-list__item--device">
                                        <span className="passkey-list__device-icon passkey-list__device-icon--windows" aria-hidden="true">
                                            <DeviceIcon />
                                        </span>
                                        <div className="passkey-list__meta">
                                            <p className="passkey-list__name">Ready to use - Sign in with passkey</p>
                                            {/* {name ? <p className="passkey-list__device-name">{name}</p> : null} */}
                                            {lines.filter(Boolean).map((line) => (
                                                <p key={line} className="passkey-list__date">{line}</p>
                                            ))}
                                        </div>
                                        <Button
                                            type="button"
                                            className="passkey-list__remove passkey-list__remove--icon"
                                            disabled={busy}
                                            aria-label={`Remove ${name}`}
                                            onClick={() => handleRemove(item)}
                                        >
                                            <X size={16} />
                                        </Button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ) : (
                    <p className="passkey-card__empty">No passkeys are saved on this account yet.</p>
                )}

                {error && supported ? (
                    <div className="passkey-alert passkey-alert--error">{error}</div>
                ) : null}
            </Card.Body>
        </Card>
    );
};

export default PasskeyRegisterCard;
